import { Check, ChevronDown, X } from 'lucide-react';
import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';

import { cn } from '@/utils/cn';

import type { ListboxOption, ListboxValue } from './listbox';
import { Popover, PopoverAnchor, PopoverContent } from './popover';
import type { PopoverPortalTarget } from './popover-portal';

/**
 * Free-text-driven selection over a provided option set.
 *
 * Differs from a searchable `Listbox` in where the text lives: here the input *is*
 * the control, which is what a long static list — countries, timezones, currencies —
 * needs. `EntityCombobox` in `shared/` extends this over the API.
 */

export interface ComboboxProps<T extends ListboxValue = string> {
  value: T | null;
  onChange: (value: T | null) => void;
  options: ListboxOption<T>[];
  placeholder?: string;
  emptyMessage?: string;
  clearable?: boolean;
  clearLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  loading?: boolean;
  id?: string;
  /** Controlled query, for a combobox whose options are fetched as the user types. */
  query?: string;
  onQueryChange?: (query: string) => void;
  /** A row appended to the results, for "add a new member" style affordances. */
  footer?: React.ReactNode;
  popoverPortal?: PopoverPortalTarget;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

export const Combobox = <T extends ListboxValue = string>({
  value,
  onChange,
  options,
  placeholder,
  emptyMessage,
  clearable = true,
  clearLabel,
  disabled = false,
  invalid = false,
  loading = false,
  id,
  query,
  onQueryChange,
  footer,
  popoverPortal = 'body',
  className,
  ...aria
}: ComboboxProps<T>) => {
  const [open, setOpen] = useState(false);
  const [internalQuery, setInternalQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const controlled = query !== undefined;
  const text = controlled ? query : internalQuery;
  const selected = options.find((option) => option.value === value) ?? null;

  const visible = useMemo(() => {
    // A controlled query means the caller is filtering (usually server-side); a
    // second filter here would hide results the server deliberately returned.
    if (controlled || !text.trim()) return options;
    const needle = text.trim().toLowerCase();
    return options.filter((option) =>
      [option.label, option.description, ...(option.keywords ?? [])]
        .filter(Boolean)
        .some((candidate) => candidate!.toLowerCase().includes(needle)),
    );
  }, [controlled, options, text]);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (next) return;
    // Closing without a choice restores the selected label, so the field never sits
    // showing text that does not match what is actually selected.
    if (!controlled) setInternalQuery('');
    setActiveIndex(0);
  };

  // Dialog focus traps and option-list remounts can ask Radix to close while the
  // input still has focus — which immediately re-opens on focus and flashes.
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      const active = document.activeElement;
      if (active && rootRef.current?.contains(active)) return;
    }
    changeOpen(next);
  };

  const eventInsideRoot = (target: EventTarget | null) =>
    Boolean(target instanceof Node && rootRef.current?.contains(target));

  const setQuery = (next: string) => {
    if (controlled) onQueryChange?.(next);
    else setInternalQuery(next);
    setActiveIndex(0);
    if (!open) changeOpen(true);
  };

  const commit = (option: ListboxOption<T> | undefined) => {
    if (!option || option.disabled) return;
    onChange(option.value);
    if (controlled) onQueryChange?.('');
    else setInternalQuery('');
    changeOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) changeOpen(true);
        else setActiveIndex((current) => Math.min(current + 1, visible.length - 1));
        return;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((current) => Math.max(current - 1, 0));
        return;
      case 'Enter':
        if (!open) return;
        event.preventDefault();
        commit(visible[activeIndex]);
        return;
      case 'Escape':
        if (open) {
          event.preventDefault();
          changeOpen(false);
        }
        return;
      case 'Backspace':
        if (text === '' && selected) onChange(null);
        return;
      default:
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <div
          ref={rootRef}
          className={cn(
            'flex w-full items-center gap-2 rounded-md border bg-surface px-3',
            'h-10',
            disabled ? 'bg-surface-sunken text-fg-disabled' : 'cursor-text',
            invalid ? 'border-danger' : 'border-border focus-within:border-brand',
            className,
          )}
          onMouseDown={(event) => {
            // Clicks on the chrome (padding, chevron) should open like focusing the
            // input. Skip the clear button — it has its own action.
            if (disabled) return;
            const target = event.target;
            if (!(target instanceof Element)) return;
            if (target.closest('button') || target === inputRef.current) return;
            event.preventDefault();
            inputRef.current?.focus();
            if (!open) changeOpen(true);
          }}
        >
          <input
            ref={inputRef}
            id={id}
            role="combobox"
            autoComplete="off"
            disabled={disabled}
            aria-expanded={open}
            aria-controls={open ? listId : undefined}
            aria-activedescendant={open ? `${listId}-option-${activeIndex}` : undefined}
            aria-invalid={invalid || undefined}
            value={open || !selected ? text : selected.label}
            placeholder={placeholder}
            onChange={(event) => setQuery(event.target.value)}
            // Do not open on focus — dialogs move focus to the first field on open,
            // and that must not drop the menu. Open on click, typing, or arrow keys.
            onFocus={(event) => {
              // Dialog autofocus also select-alls the selected label; collapse that.
              const input = event.currentTarget;
              requestAnimationFrame(() => {
                if (document.activeElement !== input) return;
                const end = input.value.length;
                input.setSelectionRange(end, end);
              });
            }}
            onClick={() => {
              if (!disabled && !open) changeOpen(true);
            }}
            onKeyDown={onKeyDown}
            className="min-w-0 flex-1 cursor-text bg-transparent text-body outline-none placeholder:text-fg-tertiary"
            {...aria}
          />

          {clearable && selected && !disabled ? (
            <button
              type="button"
              aria-label={clearLabel}
              onClick={() => {
                onChange(null);
                inputRef.current?.focus();
              }}
              className="rounded p-0.5 text-fg-tertiary hover:text-fg"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}

          <ChevronDown aria-hidden className="h-4 w-4 shrink-0 text-fg-tertiary" />
        </div>
      </PopoverAnchor>

      <PopoverContent
        popoverPortal={popoverPortal}
        role="presentation"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onFocusOutside={(event) => {
          if (eventInsideRoot(event.target)) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (eventInsideRoot(event.target)) event.preventDefault();
        }}
        className="max-h-[min(20rem,var(--radix-popover-content-available-height))] overflow-y-auto p-1"
      >
        <div id={listId} role="listbox">
          {visible.length === 0 && !loading ? (
            <p className="px-3 py-6 text-center text-body-sm text-fg-secondary">
              {emptyMessage}
            </p>
          ) : (
            visible.map((option, index) => (
              <div
                key={String(option.value)}
                id={`${listId}-option-${index}`}
                role="option"
                aria-selected={option.value === value}
                onPointerEnter={() => setActiveIndex(index)}
                onClick={() => commit(option)}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-body',
                  index === activeIndex && 'bg-surface-sunken',
                  option.disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                {option.icon}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-fg">{option.label}</span>
                  {option.description ? (
                    <span className="block truncate text-caption text-fg-secondary">
                      {option.description}
                    </span>
                  ) : null}
                </span>
                {option.value === value ? (
                  <Check aria-hidden className="h-4 w-4 shrink-0 text-brand" />
                ) : null}
              </div>
            ))
          )}
        </div>

        {footer ? <div className="mt-1 border-t border-border pt-1">{footer}</div> : null}
      </PopoverContent>
    </Popover>
  );
};
